export interface EquipmentBindingSet {
  primaryNodeId: number | null;
  candidateNodeIds: number[];
  changed: boolean;
}

const normalizeId = (value: number | null | undefined): number | null =>
  Number.isInteger(value) && Number(value) > 0 ? Number(value) : null;

const normalizeCandidates = (candidateNodeIds: number[], primaryNodeId: number | null): number[] => {
  const seen = new Set<number>();
  const result: number[] = [];
  for (const rawId of candidateNodeIds) {
    const id = normalizeId(rawId);
    if (!id || id === primaryNodeId || seen.has(id)) continue;
    seen.add(id);
    result.push(id);
  }
  return result;
};

const sameSet = (
  leftPrimary: number | null,
  leftCandidates: number[],
  rightPrimary: number | null,
  rightCandidates: number[],
): boolean =>
  leftPrimary === rightPrimary &&
  leftCandidates.length === rightCandidates.length &&
  leftCandidates.every((id, index) => id === rightCandidates[index]);

export function addOccupiedDevice(
  primaryNodeId: number | null,
  candidateNodeIds: number[],
  nodeId: number,
): EquipmentBindingSet {
  const currentPrimary = normalizeId(primaryNodeId);
  const currentCandidates = normalizeCandidates(candidateNodeIds, currentPrimary);
  const targetNodeId = normalizeId(nodeId);
  if (!targetNodeId) {
    return { primaryNodeId: currentPrimary, candidateNodeIds: currentCandidates, changed: false };
  }

  if (currentPrimary === targetNodeId) {
    return { primaryNodeId: currentPrimary, candidateNodeIds: currentCandidates, changed: false };
  }

  if (!currentPrimary) {
    const nextCandidates = currentCandidates.filter((id) => id !== targetNodeId);
    return {
      primaryNodeId: targetNodeId,
      candidateNodeIds: nextCandidates,
      changed: true,
    };
  }

  if (currentCandidates.includes(targetNodeId)) {
    return { primaryNodeId: currentPrimary, candidateNodeIds: currentCandidates, changed: false };
  }

  return {
    primaryNodeId: currentPrimary,
    candidateNodeIds: [...currentCandidates, targetNodeId],
    changed: true,
  };
}

export function removeOccupiedDevice(
  primaryNodeId: number | null,
  candidateNodeIds: number[],
  nodeId: number | null,
): EquipmentBindingSet {
  const currentPrimary = normalizeId(primaryNodeId);
  const currentCandidates = normalizeCandidates(candidateNodeIds, currentPrimary);
  const targetNodeId = normalizeId(nodeId);

  if (!targetNodeId) {
    return {
      primaryNodeId: null,
      candidateNodeIds: [],
      changed: currentPrimary !== null || currentCandidates.length > 0,
    };
  }

  const currentAll = [
    ...(currentPrimary ? [currentPrimary] : []),
    ...currentCandidates,
  ];
  if (!currentAll.includes(targetNodeId)) {
    return { primaryNodeId: currentPrimary, candidateNodeIds: currentCandidates, changed: false };
  }

  const remaining = currentAll.filter((id) => id !== targetNodeId);
  if (!remaining.length) {
    return { primaryNodeId: null, candidateNodeIds: [], changed: true };
  }

  const nextPrimary = currentPrimary && currentPrimary !== targetNodeId
    ? currentPrimary
    : remaining[0];
  const nextCandidates = remaining.filter((id) => id !== nextPrimary);

  return {
    primaryNodeId: nextPrimary,
    candidateNodeIds: nextCandidates,
    changed: !sameSet(currentPrimary, currentCandidates, nextPrimary, nextCandidates),
  };
}
